import React, { CSSProperties, useState } from 'react';
import { apiInstance } from '../../../../api/apiInstance';
import { Column } from '../../../../utils/constants/table-constants';
import DetailedJobView from '../../../../components/modals/DetailedJobView';
import JobStatus from './JobStatus';
import { DashboardData, JobResponse, JobStatusType } from 'src/pages/dashboard/util';
import BkTable from '../../../../components/table/BkTable';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/store/store';
import { changeJobStatus, updateJobTrackingNumbers } from 'src/features/admin/utils';
import ServiceBadge from 'src/components/service-badge/ServiceBadge';

const headers = [
  {
    headerName: 'Job',
    field: 'tracking',
    headerCustomStyles: {
      width: '9.375rem',
    },
  },
  {
    headerName: 'Date & Time',
    field: 'dateTime',
    headerCustomStyles: {
      width: '9.375rem',
    },
  },
  {
    headerName: 'Job Status',
    field: 'status',
    headerCustomStyles: {
      width: '11.25rem',
    },
  },
  {
    headerName: 'Organization',
    field: 'organizationName',
    headerCustomStyles: {
      width: '12.5rem',
    },
  },
  {
    headerName: 'Org Type',
    field: 'orgType',
    headerCustomStyles: {
      width: '7.5rem',
    },
  },
  {
    headerName: 'User',
    field: 'user',
    headerCustomStyles: {
      width: '6.25rem',
    },
  },
  {
    headerName: 'Service',
    field: 'service',
    headerCustomStyles: {
      width: '5.625rem',
    },
  },
  {
    headerName: 'Notice',
    field: 'type',
    headerCustomStyles: {
      width: '6.5625rem',
    },
  },
  {
    headerName: 'Case Number',
    field: 'caseNumbers',
    headerCustomStyles: {
      width: '12.5rem',
    },
  },
  {
    headerName: 'Sheets',
    field: 'sheets',
    headerCustomStyles: {
      width: '4.375rem',
    },
  },
  {
    headerName: 'Parties',
    field: 'parties',
    headerCustomStyles: {
      width: '4.375rem',
    },
  },
] as { headerName: string; field: string; headerCustomStyles?: CSSProperties }[];

interface ReportsTableProps {
  showPagination: boolean;
  data: DashboardData[];
  updateData: (data: any) => void;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const ReportsTable: React.FC<ReportsTableProps> = ({
  showPagination,
  data,
  updateData,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });
  const [fetchedJob, setFetchedJob] = useState<JobResponse | null>(null);
  const [showJobDialog, setShowJobDialog] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();

  const fetchSingleJob = async (id: number) => {
    try {
      const response = await apiInstance.get(`/job/item/${id}`);
      setFetchedJob(response.data.data);
    } catch (err) {
      setShowJobDialog(false);
    }
  };

  const onJobOpen = (jobId: number) => {
    fetchSingleJob(jobId);
    setShowJobDialog(true);
  };

  const onJobClose = () => {
    setShowJobDialog(false);
    setFetchedJob(null);
  };

  const handleCancelClick = async (id: number) => {
    dispatch(changeJobStatus({ jobId: id, newStatus: 'cancelled' })).then((result) => {
      if (changeJobStatus.fulfilled.match(result)) {
        const updatedData = data.map((item) => {
          return Number(item.id) === Number(id) ? { ...item, jobStatus: 'cancelled' } : item;
        });
        updateData(updatedData);
        fetchedJob && setFetchedJob({ ...fetchedJob, status: 'cancelled' });
        setShowSnackbarMsg({ msg: 'Job canceled successfully', type: 'success' });
      }
      if (changeJobStatus.rejected.match(result)) {
        setShowSnackbarMsg({ msg: 'Could not cancel job', type: 'error' });
      }
    });
  };

  const onStatusChange = (jobInfo: { id: number; status: JobStatusType }) => {
    dispatch(changeJobStatus({ jobId: jobInfo.id, newStatus: jobInfo.status })).then((result) => {
      if (changeJobStatus.fulfilled.match(result)) {
        const updatedData = data.map((item) => {
          return Number(item.id) === Number(jobInfo.id) ? { ...item, jobStatus: jobInfo.status } : item;
        });
        fetchSingleJob(jobInfo.id);
        updateData(updatedData);
        setShowSnackbarMsg({ msg: 'Job status changed', type: 'success' });
      }
      if (changeJobStatus.rejected.match(result)) {
        const errorMsg =
          typeof result.payload === 'string' && result.payload
            ? result.payload
            : 'Something went wrong';
        setShowSnackbarMsg({ msg: errorMsg, type: 'error' });
      }
    });
  };

  const renderTracking = (trackingNumber: string, jobId: number) => {
    return (
      <p
        style={{
          textDecoration: 'underline',
          color: '#436eb2',
          cursor: 'pointer',
          fontFamily: 'Instrument Sans, Arial',
        }}
        onClick={() => onJobOpen(jobId)}
      >
        {trackingNumber}
      </p>
    );
  };

  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'status':
        return <JobStatus status={row.jobStatus} />;
      case 'tracking':
        return renderTracking(row.tracking, row.id);
      case 'service':
        return <ServiceBadge service={row.service} />;
      default:
        return row[column.field];
    }
  };

  return (
    <div>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <BkTable
        columns={headers}
        data={data}
        initialRowsPerPage={10}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        renderCell={renderCell}
        renderPagination={showPagination}
        smallHeader
      />
      {showJobDialog && (
        <DetailedJobView
          onStatusChange={onStatusChange}
          onJobCancel={handleCancelClick}
          job={fetchedJob}
          open={showJobDialog}
          onClose={onJobClose}
        />
      )}
    </div>
  );
};

export default ReportsTable;
